import { HttpClient, type ClientOptions, type LogoBody } from '@affdice/portal-kit';

import type { AccessLogPage } from './screens/integrity/access-log-model.ts';
import type { AuditRecordPage } from './screens/integrity/audit-records-model.ts';
import type { TeamData } from './screens/operators/sections/team-model.ts';

/**
 * The platform's own endpoints.
 *
 * Separate from the operator client for the same reason that one is separate
 * from the affiliate's: an operator build carrying these paths tells whoever
 * reads the bundle that AffDice SuperAdmin exists and where it lives. §4.7's
 * reasoning about 404 over 403 applies to a JavaScript bundle as much as to a
 * status code.
 */
export interface ConsumerRow {
  readonly consumer: string;
  readonly last_event_id: string | null;
  readonly updated_at: string;
  readonly failed_count: number;
  readonly last_error: string | null;
  /** Events at or before the cursor — handled, or passed over as not its kind. */
  readonly calls: number;
  /** Events after the cursor, not yet reached. */
  readonly waiting: number;
  /**
   * Whether this job reads the events at all. The server says so, because a
   * null cursor is what a reader that never advanced and a scheduled sweep
   * both have.
   */
  readonly reads_events: boolean;
}

/** A brand the Event cursors filter can narrow to, named with its operator. */
export interface CursorBrand {
  readonly id: string;
  readonly name: string;
  readonly operator: string;
}

export interface ConsumersData {
  readonly items: readonly ConsumerRow[];
  /** Every event in the ledger — or, narrowed to a brand, every one it sent. */
  readonly total: number;
  readonly brand_id: string | null;
  readonly brands: readonly CursorBrand[];
  readonly checked_at: string;
  readonly note: string;
}

/**
 * One of an operator's periods, and what deleting it would cost.
 *
 * Three groups, matching what migration 0182 actually does: the four counts
 * that go, the three that survive with a pointer dropped, and the phrase the
 * caller has to type. `corrections_unlinked` is the one nobody expects — a
 * §28.4 correction booked into a later period, naming a line in this one,
 * which keeps its figure and loses the link. It is the only consequence of
 * this delete that recomputing the month does not undo.
 */
export interface PeriodRow {
  readonly period_id: string;
  readonly label: string;
  readonly state: string;
  readonly brand: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly commission_lines: number;
  readonly statements: number;
  readonly invoices: number;
  readonly payout_documents: number;
  readonly disputes_kept: number;
  readonly platform_invoices_kept: number;
  readonly corrections_unlinked: number;
  readonly confirm_phrase: string;
}

/** What a delete reported removing, counted before it ran. */
export type PeriodRemoval = Pick<
  PeriodRow,
  'commission_lines' | 'statements' | 'invoices' | 'payout_documents'
  | 'disputes_kept' | 'platform_invoices_kept' | 'corrections_unlinked'
>;

/** One operator profile a person holds, with the brands they are enrolled on under it. */
export interface IdentityProfile {
  readonly affiliate_id: string;
  readonly operator_id: string;
  readonly operator: string;
  readonly legal_name: string;
  readonly status: string;
  readonly brands: readonly { brand_id: string; brand: string; state: string }[];
}

/**
 * §3.11 / E40 — one mandate an affiliate has been asked for, or has granted.
 *
 * `state` is what the reader should act on: a GRANTED mandate past its
 * deadline reads EXPIRED, because nobody can use it any more and a button
 * offering to would be lying about what happens next.
 */
export interface AgentMandateRow {
  readonly id: string;
  readonly affiliate_id: string;
  readonly act: string;
  /** Why support asked. Mandatory when the request was made. */
  readonly reason: string;
  readonly state: 'REQUESTED' | 'GRANTED' | 'USED' | 'REVOKED' | 'EXPIRED';
  readonly requested_by_email: string | null;
  readonly requested_at: string;
  readonly expires_at: string | null;
  readonly used_at: string | null;
  readonly used_by_email: string | null;
  /** What the mandate produced — a dispute id, for the one act there is. */
  readonly produced_id: string | null;
}

export interface AgentMandatesData {
  readonly items: readonly AgentMandateRow[];
  readonly awaiting_you: number;
}

/**
 * §3.9d — one change support has proposed to an operator.
 *
 * `status` is what the reader should act on, not what the row literally says:
 * a PENDING proposal past its deadline is reported EXPIRED by the API, because
 * nobody can approve it any more and a queue that still offers the button is
 * lying about what will happen.
 */
export interface ChangeRequestRow {
  readonly id: string;
  readonly action_type: string;
  readonly target_type: string;
  readonly target_id: string;
  /** What the change would do, in a sentence the operator also reads. */
  readonly summary: string;
  /** Why support asked. Mandatory at proposal time. */
  readonly reason: string;
  readonly status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';
  readonly requested_by_email: string | null;
  readonly requested_at: string;
  readonly expires_at: string;
  readonly decided_at: string | null;
  readonly decided_by_email: string | null;
  readonly decision_reason: string | null;
}

export interface ChangeRequestsData {
  readonly items: readonly ChangeRequestRow[];
  readonly pending: number;
}

export interface ChainFailure {
  readonly chain_key: string;
  readonly seq: string;
  readonly id: string;
  readonly failure: string;
}

/** §8.6 (Architecture) — the AffDice SuperAdmin's own headline figures. */
export interface PlatformOverviewData {
  readonly operators_total: number;
  readonly affiliates_total: number;
  /** null until a platform-wide event-metering source exists — see §34.2a. */
  readonly events_ingested_24h: number | null;
}

export interface OperatorLifecycleCount {
  readonly state: string;
  readonly count: number;
}

/** §39.2a Layer C — the platform-wide rollup `platform_daily_kpis` answers. */
export interface PlatformActivityDay {
  readonly day: string;
  readonly clicks: number;
  readonly registrations: number;
  readonly ftds: number;
  readonly operators: number;
  readonly affiliates: number;
}

export interface PlatformActivityData {
  readonly days: number;
  readonly clicks: number;
  readonly registrations: number;
  readonly ftds: number;
  readonly operators_latest: number;
  readonly affiliates_latest: number;
  readonly daily: readonly PlatformActivityDay[];
}

export interface PlatformBillingRow {
  readonly currency: string;
  readonly issued_minor: string;
  readonly paid_minor: string;
  readonly outstanding_minor: string;
}

export class PlatformApi extends HttpClient {
  /** Where a logo path from the API is loaded from. */
  readonly apiBaseUrl: string;

  constructor(options: ClientOptions) {
    super(options);
    this.apiBaseUrl = options.baseUrl.replace(/\/+$/, '');
  }

  /**
   * Item 2 (third batch) — who is actually signed in, for the rail identity.
   * Did not exist at all; the rail showed a hardcoded "Platform" for every
   * staff member on every operator's incident alike.
   */
  me(): Promise<{ me: { id: string; name: string | null; email: string } }> {
    return this.request('GET', '/v1/platform/me');
  }

  /** The intro tour — which version this staff member has seen (null: never). */
  tour(): Promise<{ seen_version: number | null; on_behalf: boolean }> {
    return this.request('GET', '/v1/platform/me/tour');
  }

  markTourSeen(version: number): Promise<{ seen_version: number }> {
    return this.request('PUT', '/v1/platform/me/tour', { body: { version } });
  }

  /**
   * Cross-tenant headline figures. The audit chain and consumer health are
   * deliberately not part of this response — the Dashboard reads those from
   * `verifyChain()` / `consumers()` directly, the same calls the Audit and
   * Consumers tabs already make, rather than a second definition of the same
   * numbers.
   */
  overview(): Promise<{
    data: PlatformOverviewData;
    data_as_of: string;
    unavailable_because?: string;
  }> {
    return this.request('GET', '/v1/platform/overview');
  }

  /** §50.5a — how many tenants, by lifecycle state, and how many are stuck. */
  operatorsSummary(): Promise<{
    by_state: OperatorLifecycleCount[];
    blocked_on_go_live: number;
    data_as_of: string;
  }> {
    return this.request('GET', '/v1/platform/operators/summary');
  }

  /**
   * §39.2a Layer C — clicks/registrations/FTDs across every tenant, read
   * from `platform_daily_kpis`. The one figure group on this Dashboard with
   * no live query behind it at all: `null` until the rollup has run once.
   */
  activitySummary(): Promise<{
    data: PlatformActivityData | null;
    data_as_of: string;
    unavailable_because?: string;
  }> {
    return this.request('GET', '/v1/platform/activity');
  }

  /** Ch. 47 — platform invoice totals, grouped by currency, never summed across them. */
  billingSummary(): Promise<{
    by_currency: PlatformBillingRow[];
    data_as_of: string;
    note: string;
  }> {
    return this.request('GET', '/v1/platform/billing/summary');
  }

  /** §3.2, §3.9 — who can sign in here, and what each may do. */
  staff(search?: string): Promise<{
    items: {
      id: string;
      email: string;
      actor_class: string;
      status: string;
      roles: readonly string[];
      mfa_enrolled: boolean;
      must_change_password: boolean;
    }[];
  }> {
    return this.request('GET', '/v1/admin/staff', {
      query: search ? { search } : {},
    });
  }

  /** §3.9 — only what this side may grant. */
  roles(): Promise<{
    items: { id: string; code: string; permissions: readonly string[] }[];
    note: string;
  }> {
    return this.request('GET', '/v1/admin/roles');
  }

  addStaff(body: { email: string; role_id: string; temporary_password: string }): Promise<{
    email: string;
    role: string;
    next_steps: string;
  }> {
    return this.request('POST', '/v1/admin/staff', { body });
  }

  /**
   * §3.9 — reversible unless `permanent` is set, in which case
   * `confirm_email` must match the staff member's own address exactly
   * (checked server-side either way).
   */
  removeStaff(
    userId: string,
    body: { permanent: boolean; confirm_email?: string },
  ): Promise<{ removed?: boolean; purged?: boolean; user_id: string }> {
    return this.request('DELETE', `/v1/admin/staff/${userId}`, { body });
  }

  /**
   * §3.7 — clears a lost or replaced authenticator, so they are asked to set
   * one up again on their next sign-in. Not an MFA opt-out: the account still
   * requires a second factor, it simply no longer has one.
   */
  resetMfa(userId: string): Promise<{ reset: boolean; user_id: string; message: string }> {
    return this.request('POST', `/v1/admin/staff/${userId}/mfa-reset`, {});
  }

  operators(search?: string): Promise<{
    items: {
      id: string;
      legal_name: string;
      display_name: string;
      base_currency: string;
      /** Item 3 (third batch) — null until a superadmin sets it. */
      country: string | null;
      accounting_timezone: string;
      approval_status: string;
      lifecycle_state: string;
      joined: string;
      brands: number;
      /** Distinct affiliate emails with a profile at this operator, any status. */
      affiliates: number;
      owner_name: string | null;
      owner_email: string | null;
      owner_phone: string | null;
    }[];
  }> {
    return this.request('GET', '/v1/platform/operators', {
      query: search ? { search } : {},
    });
  }

  /** Migration 0111 — self-signups sitting at approval_status = PENDING. */
  pendingOperators(): Promise<{
    items: {
      id: string;
      legal_name: string;
      display_name: string;
      base_currency: string;
      joined: string;
      owner_name: string | null;
      owner_email: string | null;
      owner_phone: string | null;
    }[];
  }> {
    return this.request('GET', '/v1/platform/operators/pending', {});
  }

  /** `operator_id` in the query string: see `operatorDeletion`'s comment on why. */
  approveOperator(id: string): Promise<{ operator_id: string; approval_status: string }> {
    return this.request('POST', `/v1/platform/operators/${id}/approve`, {
      query: { operator_id: id },
    });
  }

  /** `operator_id` in the query string: see `operatorDeletion`'s comment on why. */
  rejectOperator(id: string, reason?: string): Promise<{ operator_id: string; approval_status: string }> {
    return this.request('POST', `/v1/platform/operators/${id}/reject`, {
      query: { operator_id: id },
      body: reason ? { reason } : {},
    });
  }

  /**
   * `operator_id` in the query string is not decoration — it is what puts
   * the request inside that operator's row-level-security context on the
   * server (server.ts's dispatch reaches for withPlatformStaff only when a
   * PLATFORM_STAFF caller supplies it). Delete and restore are reversible;
   * purge is refused until the operator has stayed deleted for the whole
   * retention window. Delete and purge also need `confirm_name`, the
   * operator's exact display_name; every one needs a reason.
   */
  operatorDeletion(
    id: string,
    act: 'delete' | 'restore' | 'purge',
    body: { reason: string; confirm_name?: string },
  ): Promise<{ operator_id: string; state?: string; lifecycle_state?: string }> {
    return this.request('POST', `/v1/platform/operators/${id}/${act}`, {
      query: { operator_id: id },
      body,
    });
  }

  /** Item 7 (third batch), §50.2. `operator_id` in the query string: see `operatorDeletion`'s comment on why. */
  setOperatorStatus(
    id: string,
    action: 'SUSPEND' | 'REACTIVATE',
    reason: string,
  ): Promise<{ operator_id: string; state: string }> {
    return this.request('POST', `/v1/platform/operators/${id}/status`, {
      query: { operator_id: id },
      body: { action, reason },
    });
  }

  /** Ch. 50 — onboarding a tenant. It arrives inert (§50.1a). */
  createOperator(body: {
    legal_name: string;
    display_name: string;
    base_currency: string;
    /** Item 3 (third batch) — ISO 3166-1 alpha-2, e.g. "GB". */
    country: string;
    owner_email: string;
    owner_temporary_password: string;
    owner_name?: string;
    owner_phone?: string;
  }): Promise<{ operator_id: string; owner_email: string; state: string; next_steps: string }> {
    return this.request('POST', '/v1/platform/operators', { body });
  }

  /** Item 3 (third batch), superadmin-only (platform.admin). `operator_id` in the query string: see `operatorDeletion`'s comment on why. */
  setOperatorCountry(id: string, country: string): Promise<{ operator_id: string; country: string }> {
    return this.request('PUT', `/v1/platform/operators/${id}/country`, {
      query: { operator_id: id },
      body: { country },
    });
  }

  /** §50.5a — the gate, as the checklist the platform evaluates. */
  operatorDetail(operatorId: string): Promise<{
    operator: Record<string, unknown>;
    counts: Record<string, number>;
    conditions: {
      condition_no: number;
      is_met: boolean;
      detail: string;
      waiver_reason: string | null;
      waivable: boolean;
    }[];
    note: string;
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}`);
  }

  /**
   * §3.9a / §3.9e / E30 — this operator's own affiliates, the same profile
   * fields (locale, dormancy, active brand enrollments) the affiliate sees
   * on their own console. Every call is logged per access (§3.9c)
   * server-side; the `note` in the response says so to whoever is reading
   * it, not only to the audit trail.
   */
  operatorAffiliates(operatorId: string): Promise<{
    operator: string;
    items: {
      id: string; legal_name: string; country: string; status: string;
      pending_reason: string | null; is_dormant: boolean; shown_to_affiliate: string;
      email: string; locale: string; joined: string;
      deleted: boolean; purged: boolean;
      brands: { brand_id: string; brand: string }[];
      /** Batch 4 item 2 — the same widening the operator's own report got. */
      clicks: number; bot_clicks: number; players: number;
      commission_minor: string; currency: string;
    }[];
    note: string;
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/affiliates`);
  }

  /** §3.9a / §3.9e / E30 — this operator's own brands, same list they see themselves. */
  operatorBrands(operatorId: string, includeDeleted = false): Promise<{
    operator: string;
    items: {
      brand_id: string; code: string; display_name: string; base_currency: string;
      tracking_domain: string; status: string; products: string[];
      /** §12.4 — `null` is the standard colour, not the absence of one. */
      theme_accent: string | null;
      /** Brand profile (0331) — web app, website, and the logo's API path (null when none). */
      default_url: string | null;
      website_url: string | null;
      logo_url: string | null;
      /** Present only when `includeDeleted` was asked for. */
      soft_deleted_at?: string | null;
      purged_at?: string | null;
    }[];
    note: string;
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/brands`, {
      query: includeDeleted ? { include_deleted: '1' } : {},
    });
  }

  /**
   * Every brand across operators, deleted and purged ones last and flagged —
   * the Brands page. A directory read: names and settings, never a deal or a
   * commission.
   */
  platformBrands(search?: string, operatorId?: string): Promise<{
    items: {
      brand_id: string; operator_id: string; operator: string; operator_state: string;
      code: string; display_name: string; base_currency: string; tracking_domain: string;
      status: string; theme_accent: string | null; default_url: string | null;
      /** Brand profile (0331) — the public website, and the logo's API path (null when none). */
      website_url: string | null; logo_url: string | null;
      products: string[];
      soft_deleted_at: string | null; purged_at: string | null;
    }[];
  }> {
    return this.request('GET', '/v1/platform/brands', {
      query: {
        ...(search ? { search } : {}),
        ...(operatorId ? { operator_id: operatorId } : {}),
      },
    });
  }

  /**
   * A brand's logo: the original and a JPEG rendition, validated by the
   * server. A direct edit of an operator's data, so the reason is recorded
   * and the operator told (3.9d).
   */
  setBrandLogo(brandId: string, body: LogoBody, reason: string): Promise<{ logo_url: string }> {
    return this.request('PUT', `/v1/platform/brands/${brandId}/logo`, { body: { ...body, reason } });
  }

  removeBrandLogo(brandId: string, reason: string): Promise<void> {
    return this.request('DELETE', `/v1/platform/brands/${brandId}/logo`, { body: { reason } });
  }

  /**
   * §12.4 — a brand's own accent colour, set directly rather than proposed:
   * §3.9d gates a brand's commercial terms behind the operator's own
   * approval, and a colour is not one of those, so there is nothing here for
   * the operator to approve.
   */
  /** What the signed-in platform user may do (3.12) — `mine` from the permissions read. */
  async myCapabilities(): Promise<readonly string[]> {
    const r = await this.request<{ mine: readonly string[] }>('GET', '/v1/platform/permissions');
    return r.mine;
  }

  /** An operator's team, with the roles and brands its forms offer. Logged per read (3.9c). */
  operatorTeam(operatorId: string): Promise<TeamData> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/staff`, {
      query: { operator_id: operatorId },
    });
  }

  /** Adds a member. No password is sent or returned: the server mails one to the person alone. */
  addOperatorStaff(
    operatorId: string,
    body: { email: string; role_id: string; brand_ids: string[]; reason: string; name?: string },
  ): Promise<{ user_id: string; email: string; role: string; next_steps: string }> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/staff`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /** Name and role. The owner keeps the owner role. */
  editOperatorStaff(operatorId: string, userId: string, body: { name?: string; role_id?: string; reason: string }): Promise<unknown> {
    return this.request('PUT', `/v1/platform/operators/${operatorId}/staff/${userId}`, { query: { operator_id: operatorId }, body });
  }

  /** The brands a member is limited to; an empty list lifts the limit. */
  setOperatorStaffBrands(operatorId: string, userId: string, body: { brand_ids: string[]; reason: string }): Promise<unknown> {
    return this.request('PUT', `/v1/platform/operators/${operatorId}/staff/${userId}/brands`, { query: { operator_id: operatorId }, body });
  }

  disableOperatorStaff(operatorId: string, userId: string, body: { reason: string }): Promise<unknown> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/staff/${userId}/disable`, { query: { operator_id: operatorId }, body });
  }

  enableOperatorStaff(operatorId: string, userId: string, body: { reason: string }): Promise<unknown> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/staff/${userId}/enable`, { query: { operator_id: operatorId }, body });
  }

  /** Clears their authenticator and signs them out; the password is untouched. */
  resetOperatorStaffMfa(operatorId: string, userId: string, body: { reason: string }): Promise<unknown> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/staff/${userId}/mfa-reset`, { query: { operator_id: operatorId }, body });
  }

  lockOperatorStaff(operatorId: string, userId: string, body: { reason: string }): Promise<unknown> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/staff/${userId}/lock`, { query: { operator_id: operatorId }, body });
  }

  unlockOperatorStaff(operatorId: string, userId: string, body: { reason: string }): Promise<unknown> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/staff/${userId}/unlock`, { query: { operator_id: operatorId }, body });
  }

  /**
   * Permanently removes a member, the owner included. Past records keep the
   * person's name. A sole owner needs `successor_id`, another active member.
   */
  deleteOperatorStaff(
    operatorId: string,
    userId: string,
    body: { reason: string; confirm_email: string; successor_id?: string },
  ): Promise<{ deleted: boolean; successor_id: string | null }> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/staff/${userId}/delete`, { query: { operator_id: operatorId }, body });
  }

  setBrandTheme(
    operatorId: string,
    brandId: string,
    body: { theme_accent: string | null; reason?: string },
  ): Promise<{ brand_id: string; theme_accent: string | null }> {
    return this.request('PUT', `/v1/platform/operators/${operatorId}/brands/${brandId}/theme`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /**
   * This operator's periods, with what each one holds.
   *
   * `operator_id` in the query string as well as in the path: see
   * `operatorDeletion`'s comment on why the server insists on the handshake
   * rather than reading the path — it is what puts the request inside that
   * operator's row-level-security context, which this read needs because
   * `periods` is a TENANT table.
   */
  operatorPeriods(operatorId: string): Promise<{
    operator: string;
    items: PeriodRow[];
    note: string;
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/periods`, {
      query: { operator_id: operatorId },
    });
  }

  /**
   * Delete one period and everything computed inside it.
   *
   * Only here, never in the operator's own console: §19.5a and §22.6 forbid
   * altering a locked period, and an operator who could reach this could
   * un-invoice an affiliate. The server gates it on `platform.admin`.
   *
   * `confirm_phrase` is the `confirm_phrase` the list returned for this row,
   * typed back by the caller. Composed server-side on purpose, so the hint the
   * screen shows and the string the server insists on cannot drift apart.
   */
  deletePeriod(
    operatorId: string,
    periodId: string,
    body: { confirm_phrase: string },
  ): Promise<{
    deleted: boolean;
    period_id: string;
    label: string;
    brand: string;
    removed: PeriodRemoval;
    note: string;
  }> {
    return this.request('DELETE', `/v1/platform/operators/${operatorId}/periods/${periodId}`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /**
   * §3.9a / §3.9e / E30 — this operator's own headline figures, same query their Dashboard runs.
   *
   * `brandId` narrows the same read to one brand. Nothing new on the server:
   * the route already parses `brand_id` alongside the date range, and
   * `overview` has always filtered every figure by it — this console simply
   * never asked. Omitted, the answer is the operator's, exactly as before.
   */
  operatorAnalytics(operatorId: string, range?: { from: string; to: string }, brandId?: string): Promise<{
    operator: string;
    data: {
      clicks: number;
      customers: number;
      ftds: number;
      registration_cr_pct: number | null;
      paid_minor: string;
      outstanding_payable_minor: string;
      currency: string | null;
      deposits_minor: string | null;
      /** Null with no brand feed yet, the same as deposits and GGR. */
      deposits_count: number | null;
      ftd_amount_minor: string | null;
      activities: number | null;
      ggr_minor: string | null;
      ngr_minor: string | null;
      attribution_gap_because?: string | null;
    };
    data_as_of: string;
    is_provisional: boolean;
    provisional_because?: string;
    unavailable_because?: string;
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/analytics`, {
      query: {
        ...(range ? { from: range.from, to: range.to } : {}),
        ...(brandId ? { brand_id: brandId } : {}),
      },
    });
  }

  /**
   * §3.9 — names and states across operators. Not a way into their terms.
   * Two rows can legitimately share `identity_id`: the same person holding
   * a separate profile at two operators (§6.7) — not a duplicate to merge.
   */
  /**
   * Item 8 (third batch) — one row per person, not per operator
   * relationship. Each row carries every relationship that person holds, so
   * a row click can offer the choice rather than the list showing the same
   * name three times.
   */
  affiliates(search?: string, operatorId?: string, sort?: string): Promise<{
    items: {
      identity_id: string;
      display_name: string;
      email: string;
      country: string;
      /** The platform-wide identity status (ACTIVE/SUSPENDED/CLOSED), not any one operator's. */
      identity_status: string;
      operators: {
        affiliate_id: string;
        operator_id: string;
        operator: string;
        legal_name: string;
        status: string;
        shown_to_affiliate: string;
        joined: string;
        /** Soft-deleted (restorable) or purged (gone for good) — listed so it can be found. */
        deleted: boolean;
        purged: boolean;
      }[];
      /** Item 8 — brand names across every operator this person works with. */
      brands: string[] | string;
      /** Earliest of their relationships. */
      joined: string;
      /** Item 9 — what "most active" sorts on. */
      clicks: number;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/platform/affiliates', {
      query: {
        ...(search ? { search } : {}),
        ...(operatorId ? { operator_id: operatorId } : {}),
        ...(sort ? { sort } : {}),
      },
    });
  }

  /**
   * §3.9a / §3.9e / E30 — an affiliate's own "whole picture": profile,
   * money, deal terms (revshare/CPA/tiers) and brand enrollments. Same read
   * `affiliateOverview` gives the operator's own staff; logged per access.
   */
  /**
   * One affiliate's own dashboard — the same `overview()` read as
   * `operatorAnalytics`, narrowed to this affiliate and, given one, to one
   * brand. Someone who earned on two brands needs one named: NGR is never
   * blended across brands, and the server says so rather than guessing.
   */
  affiliateAnalytics(affiliateId: string, brandId?: string): Promise<{
    affiliate: string;
    data: Awaited<ReturnType<PlatformApi['operatorAnalytics']>>['data'];
    data_as_of: string;
    is_provisional: boolean;
    provisional_because?: string;
    unavailable_because?: string;
  }> {
    return this.request('GET', `/v1/platform/affiliates/${affiliateId}/analytics`, {
      query: brandId ? { brand_id: brandId } : {},
    });
  }

  /**
   * `brandId` narrows money, and the click and player counts, to that one
   * brand. Links, documents, registrations and terms still cover every brand.
   */
  affiliateDetail(affiliateId: string, brandId?: string): Promise<{
    affiliate: {
      id: string; legal_name: string; country: string; status: string;
      shown_to_affiliate: string; pending_reason: string | null; is_dormant: boolean;
      joined: string; email: string; locale: string;
      /** Item 7 (third batch) — the platform-wide identity behind this one operator relationship. */
      identity_id: string; identity_status: string;
      /** The operator's own fields on this affiliate — what a direct edit (3.9d) starts from. */
      username?: string | null; external_affiliate_id?: string | null;
      company_name?: string | null; contact_name?: string | null; phone?: string | null;
      website_urls?: string | null; marketing_note?: string | null;
      geos?: string[] | null;
      tags?: { id: string; name: string }[];
    };
    /** 9.1 — who manages this affiliate now (PRIMARY, SECONDARY). */
    managers?: { role: string; manager_id: string; manager: string; valid_from: string }[];
    money: Record<string, unknown>;
    money_by_brand: Record<string, unknown>[];
    currency: string | null;
    documents: Record<string, unknown>;
    traffic: Record<string, unknown>;
    registrations: Record<string, unknown>;
    terms: {
      brand: string;
      commission_type: string;
      revshare_rate_bps: number | null;
      cpa_amount_minor: string | null;
      state: string;
    }[];
    /** Items 6/7 (third batch) — `state` is ACTIVE or SUSPENDED (WITHDRAWN is filtered out server-side). */
    brands: { brand_id: string; brand: string; state: string; enrolled: string }[];
    /** Item 7 (third batch) — this affiliate's own operator, to pass as ?operator_id= to changeAffiliateStatus. */
    operator_id: string;
    /** When this profile was soft-deleted, or null; and when it was purged, or null. */
    deleted_at: string | null;
    purged_at: string | null;
    note: string;
  }> {
    return this.request('GET', `/v1/platform/affiliates/${affiliateId}`, {
      query: brandId ? { brand_id: brandId } : {},
    });
  }

  /**
   * Item 7 (third batch) — the same per-operator status change an
   * operator's own staff already has (§6.2), reachable from AMS too.
   * `operator_id` in the query string: see `operatorDeletion`'s comment on why.
   */
  changeAffiliateStatus(
    operatorId: string,
    affiliateId: string,
    body: { to: string; reasonCode: string; justification?: string; force?: boolean },
  ): Promise<{ status: string; note?: string }> {
    return this.request('POST', `/v1/platform/affiliates/${affiliateId}/status`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /**
   * §6.14 — the narrowest of the three scopes: this affiliate, on this one
   * brand. Their standing on every other brand, their operator-wide status
   * (`changeAffiliateStatus` above) and their platform-wide identity
   * (`changeIdentityStatus` below) are all untouched by it.
   *
   * Same `operator_id` handshake as `changeAffiliateStatus`, and for the same
   * reason: the enrollment row is that operator's own.
   */
  setBrandEnrollmentStatus(
    operatorId: string,
    affiliateId: string,
    brandId: string,
    state: 'ACTIVE' | 'SUSPENDED',
    reasonCode: string,
  ): Promise<{ brand_id: string; affiliate_id: string; state: string }> {
    return this.request(
      'POST',
      `/v1/platform/affiliates/${affiliateId}/brands/${brandId}/status`,
      { query: { operator_id: operatorId }, body: { state, reasonCode } },
    );
  }

  /**
   * Item 7 (third batch) — the platform-wide scope: every operator profile
   * this person holds, at once. Distinct from `changeAffiliateStatus` above,
   * which only ever changes one.
   */
  changeIdentityStatus(
    identityId: string,
    status: 'ACTIVE' | 'SUSPENDED' | 'CLOSED',
    reason: string,
  ): Promise<{ identity_id: string; status: string }> {
    return this.request('POST', `/v1/platform/identities/${identityId}/status`, {
      body: { status, reason },
    });
  }

  /**
   * Item 6 (third batch) — this affiliate's invoices and disputes, read
   * cross-tenant and logged per access, same as the two reads above.
   */
  affiliateInvoices(affiliateId: string): Promise<{
    data: {
      id: string; invoice_number: string; status: string;
      total_minor: string; settled_minor: string; currency: string;
      due_at: string | null; affiliate: string;
    }[];
  }> {
    return this.request('GET', `/v1/platform/affiliates/${affiliateId}/invoices`);
  }

  affiliateDisputes(affiliateId: string): Promise<{
    data: {
      id: string; category: string; kind: string; status: string;
      outcome: string | null; what_happened: string;
      disputed_amount_minor: string | null; currency: string | null;
      raised_at?: string;
    }[];
  }> {
    return this.request('GET', `/v1/platform/affiliates/${affiliateId}/disputes`);
  }

  affiliateLinks(affiliateId: string): Promise<{
    data: {
      link_id: string;
      campaign: string;
      brand: string;
      created: string;
      revoked: boolean;
      clicks: number;
      bot_clicks: number;
      last_click: string | null;
    }[];
    note: string;
  }> {
    return this.request('GET', `/v1/platform/affiliates/${affiliateId}/links`);
  }

  /**
   * Batch 4 item 3 — deleting a brand belongs to AMS, not to the operator,
   * whose console keeps activate/deactivate and nothing destructive.
   * `operator_id` in the query string: see `operatorDeletion`'s comment on why.
   */
  brandDeletion(
    operatorId: string,
    brandId: string,
    act: 'delete' | 'restore' | 'purge',
    body: { reason: string; confirm_name?: string },
  ): Promise<{ brand_id: string }> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/brands/${brandId}/${act}`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /**
   * Delete, restore or purge one affiliate's profile with one operator. A
   * flag beside the status, not a new status; the person's other operators
   * are untouched. Every act needs a reason.
   */
  affiliateDeletion(
    operatorId: string,
    affiliateId: string,
    act: 'delete' | 'restore' | 'purge',
    body: { reason: string },
  ): Promise<{ affiliate_id?: string }> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/affiliates/${affiliateId}/${act}`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /**
   * §3.11 — ask an affiliate for authority to act in their name, once.
   *
   * §3.9d's own remedy — propose, and the operator approves — is the wrong
   * counterparty for a dispute: the operator is the party being disputed.
   * So this asks the affiliate, and only they can answer.
   */
  requestAgentMandate(
    operatorId: string,
    affiliateId: string,
    body: { act: string; reason: string },
  ): Promise<{ mandate_id: string; state: string; message: string }> {
    return this.request('POST', `/v1/platform/affiliates/${affiliateId}/agent-mandates`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /** What has been asked of this affiliate, and what became of each. */
  agentMandates(operatorId: string, affiliateId: string): Promise<AgentMandatesData> {
    return this.request('GET', `/v1/platform/affiliates/${affiliateId}/agent-mandates`, {
      query: { operator_id: operatorId },
    });
  }

  /**
   * §3.11c — use a granted mandate. The dispute is the affiliate's; the act is
   * recorded against whoever here performed it.
   */
  raiseDisputeForAffiliate(
    operatorId: string,
    mandateId: string,
    body: {
      category: string;
      what_happened: string;
      context_brand_id?: string;
    },
  ): Promise<{ mandate_id: string; state: string; affiliate_id: string; message: string }> {
    return this.request('POST', `/v1/platform/agent-mandates/${mandateId}/raise-dispute`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /**
   * §3.9d — propose a change for the operator to approve.
   *
   * Support can read every tenant and may act on none of them, so this console
   * has no endpoint that changes an operator's commercial data and never will.
   * What it has instead is this: the change is described, the operator is
   * notified, and one of *their* people applies it. `operator_id` in the query
   * string for the reason `deleteBrand` gives — it is what puts the write
   * inside that operator's row-level-security context.
   */
  /**
   * §3.9d — a direct edit: the operator's own write route, inside that
   * operator (`operator_id`), with the reason in the header the server reads
   * for every direct edit (platform-edit.ts). Audited under the platform role
   * and notified to the operator by the server.
   */
  directEdit<T>(method: string, path: string, operatorId: string, body: unknown, reason: string): Promise<T> {
    return this.request<T>(method, path, {
      query: { operator_id: operatorId },
      body,
      headers: { 'x-platform-reason': encodeURIComponent(reason) },
    });
  }

  /** §3.9d, §12.2 — one brand's setting, changed now rather than proposed. */
  changeBrandSetting(
    operatorId: string,
    brandId: string,
    key: string,
    change: { value: unknown; effective_from?: string } | { clear: true },
    reason: string,
  ): Promise<unknown> {
    return this.directEdit('PUT', `/v1/admin/settings/${encodeURIComponent(key)}`, operatorId,
      { ...change, brand_id: brandId, reason }, reason);
  }

  /** §3.9d, 6.2 — an affiliate's status, moved through the operator's own state machine. */
  changeAffiliateStatusDirect(
    operatorId: string,
    affiliateId: string,
    body: { to: string; reasonCode: string; force?: boolean; justification?: string },
    reason: string,
  ): Promise<unknown> {
    return this.directEdit('POST', `/v1/admin/affiliates/${affiliateId}/status`, operatorId, body, reason);
  }

  /** §3.9d — the operator's own fields on an affiliate; only the ones sent change. */
  setAffiliateProfileFields(operatorId: string, affiliateId: string, body: Record<string, unknown>, reason: string): Promise<unknown> {
    return this.directEdit('PUT', `/v1/admin/affiliates/${affiliateId}/profile-fields`, operatorId, body, reason);
  }

  /** §3.9d — the whole tag list; an empty list removes every tag. */
  setAffiliateTags(operatorId: string, affiliateId: string, tags: string[], reason: string): Promise<unknown> {
    return this.directEdit('PUT', `/v1/admin/affiliates/${affiliateId}/tags`, operatorId, { tags }, reason);
  }

  /** §3.9d, 9.1 — a manager from the operator's own team, from now. */
  assignAffiliateManager(
    operatorId: string,
    affiliateId: string,
    body: { managerId: string; role: 'PRIMARY' | 'SECONDARY' },
    reason: string,
  ): Promise<unknown> {
    return this.directEdit('POST', `/v1/admin/affiliates/${affiliateId}/manager`, operatorId, body, reason);
  }

  /** §3.9d — a note on the affiliate, internal or shown to them. */
  addAffiliateNote(
    operatorId: string,
    affiliateId: string,
    body: { body: string; visibility: 'INTERNAL' | 'AFFILIATE_VISIBLE' },
    reason: string,
  ): Promise<unknown> {
    return this.directEdit('POST', `/v1/admin/affiliates/${affiliateId}/notes`, operatorId, body, reason);
  }

  /** §3.9d — the operator's company name and address; only the fields sent change. */
  setOperatorCompany(operatorId: string, body: Record<string, string | null>, reason: string): Promise<unknown> {
    return this.directEdit('PUT', '/v1/admin/profile/company', operatorId, body, reason);
  }

  /** The operator-level settings, as the operator's own Settings screen lists them. Logged per access. */
  operatorSettings(operatorId: string): Promise<{
    items: {
      key: string;
      description: string;
      kind: 'number' | 'string' | 'boolean' | 'object';
      platform_default: unknown;
      value: unknown;
      resolved_from: 'PLATFORM' | 'OPERATOR' | 'BRAND';
      options?: readonly string[];
      scheduled?: { id: string; value: unknown; effective_from: string; version: number };
    }[];
    accounting_timezone: string;
    today: string;
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/settings`, {
      query: { operator_id: operatorId },
    });
  }

  /** §3.9d, §12.2 — one operator-level setting, changed now. */
  changeOperatorSetting(
    operatorId: string,
    key: string,
    change: { value: unknown; effective_from?: string } | { clear: true },
    reason: string,
  ): Promise<unknown> {
    return this.directEdit('PUT', `/v1/admin/settings/${encodeURIComponent(key)}`, operatorId,
      { ...change, reason }, reason);
  }

  /** §3.9d — a brand's name, base currency or default page. */
  updateBrandProfile(operatorId: string, brandId: string, body: Record<string, string | null>, reason: string): Promise<unknown> {
    return this.directEdit('PATCH', `/v1/admin/brands/${brandId}`, operatorId, body, reason);
  }

  /** §3.9d — switch a brand on or off. */
  setBrandStatus(operatorId: string, brandId: string, status: 'ACTIVE' | 'INACTIVE', reason: string): Promise<unknown> {
    return this.directEdit('PUT', `/v1/admin/brands/${brandId}/status`, operatorId, { status }, reason);
  }

  /** One operator's campaigns, latest version's state. Logged per access. */
  operatorCampaigns(operatorId: string): Promise<{
    items: {
      campaign_id: string; code: string; name: string; brand_name: string;
      state: string | null; current_version: number | null; updated_at: string;
    }[];
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/campaigns`, {
      query: { operator_id: operatorId },
    });
  }

  /** One operator's deals, with the version in force and any offer awaiting an answer. Logged per access. */
  operatorDeals(operatorId: string): Promise<{
    data: {
      deal_id: string; code: string; name: string | null; brand: string; affiliate: string | null;
      live_version: number | null; live_type: string | null; live_valid_to: string | null;
      ended_at: string | null; reactivated_as: string | null; pending_version: number | null;
    }[];
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/deals`, {
      query: { operator_id: operatorId },
    });
  }

  /** §3.9d — pause, resume or retire a campaign. */
  campaignAct(operatorId: string, campaignId: string, act: 'pause' | 'resume' | 'retire', reason: string): Promise<unknown> {
    return this.directEdit('POST', `/v1/admin/campaigns/${campaignId}/${act}`, operatorId, {}, reason);
  }

  /** §3.9d, 7.3 — end a deal now; the reason is the deal's own record of why too. */
  endDeal(operatorId: string, dealId: string, reason: string): Promise<unknown> {
    return this.directEdit('POST', `/v1/admin/deals/${dealId}/end`, operatorId, { reason }, reason);
  }

  /** §3.9d — re-offer an ended deal's last agreed terms from a day; the affiliate answers it. */
  reactivateDeal(operatorId: string, dealId: string, validFrom: string, reason: string): Promise<unknown> {
    return this.directEdit('POST', `/v1/admin/deals/${dealId}/reactivate`, operatorId, { valid_from: validFrom }, reason);
  }

  proposeChange(
    operatorId: string,
    body: {
      action_type: string;
      target_id: string;
      payload: Record<string, unknown>;
      reason: string;
    },
  ): Promise<{ change_request_id: string; status: string; expires_at: string; message: string }> {
    return this.request('POST', `/v1/platform/operators/${operatorId}/change-requests`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /**
   * §3.9d — one brand's settings, read-only. `config_values` grants platform
   * staff SELECT and nothing else; changing one is a proposal the operator
   * approves.
   *
   * §12.2 — `scheduled` is the earliest not-yet-started row at this brand,
   * and `accounting_timezone`/`today` are what the proposal form's own
   * "Applies from" date defaults to and names.
   */
  brandSettings(operatorId: string, brandId: string): Promise<{
    items: {
      key: string;
      description: string;
      kind: 'number' | 'string' | 'boolean' | 'object';
      platform_default: unknown;
      value: unknown;
      resolved_from: 'PLATFORM' | 'OPERATOR' | 'BRAND';
      inherited_value?: unknown;
      inherited_from?: 'PLATFORM' | 'OPERATOR';
      options?: readonly string[];
      set_at?: string;
      version?: number;
      scheduled?: {
        id: string;
        value: unknown;
        effective_from: string;
        version: number;
      };
    }[];
    accounting_timezone: string;
    today: string;
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/brands/${brandId}/settings`, {
      query: { operator_id: operatorId },
    });
  }

  /**
   * §4.2e / §12.2 — one setting's own history at this brand: every value it
   * has held, who set it, why, and whether it was withdrawn before it ever
   * started. Includes the operator's own rows too (§4.2e), so a brand's
   * history is not missing the value it fell back to before it set one.
   */
  brandSettingHistory(operatorId: string, brandId: string, key: string): Promise<{
    key: string;
    accounting_timezone: string;
    items: {
      id: string;
      level: 'OPERATOR' | 'BRAND';
      value: unknown;
      version: number;
      valid_from: string;
      valid_to: string | null;
      state: 'SCHEDULED' | 'IN_FORCE' | 'ENDED' | 'WITHDRAWN';
      set_by: string | null;
      reason: string | null;
      created_at: string;
      withdrawn_at?: string;
      withdrawn_by?: string | null;
    }[];
  }> {
    return this.request(
      'GET',
      `/v1/platform/operators/${operatorId}/brands/${brandId}/settings/${key}/history`,
      { query: { operator_id: operatorId } },
    );
  }

  /**
   * §8.9 / §12.3 — this operator's sub-affiliate programme and its referral
   * edges, read-only (AMS only reads sub-affiliate networks — it neither
   * recruits nor sets a rate).
   */
  operatorReferrals(operatorId: string): Promise<{
    programme: {
      enabled: boolean;
      depth: number;
      cap_bps: number;
      default_override_bps: number;
    };
    items: {
      id: string;
      recruiter_id: string;
      recruiter_name: string;
      sub_id: string;
      sub_name: string;
      depth: number;
      override_bps: number;
      basis: 'SUB_COMMISSION' | 'SUB_NGR';
      lifetime_policy: 'FIXED_TERM' | 'LIFETIME' | 'ACTIVE_ONLY';
      valid_from: string;
      valid_to: string | null;
      active: boolean;
    }[];
  }> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/referrals`, {
      query: { operator_id: operatorId },
    });
  }

  /** What support proposed for this operator, and what became of each one. */
  changeRequests(operatorId: string): Promise<ChangeRequestsData> {
    return this.request('GET', `/v1/platform/operators/${operatorId}/change-requests`, {
      query: { operator_id: operatorId },
    });
  }

  /** §42.3 — a tamper-evident log nobody verifies is tamper-hoping. */
  /** Integrity → Access log: what platform staff did across operators, newest first, a page at a time. */
  accessLog(query: Record<string, string>): Promise<AccessLogPage> {
    return this.request('GET', '/v1/platform/access-log', { query });
  }

  /** The records on the chain an administrator should know about, by group, a page at a time. */
  auditRecords(query: Record<string, string>): Promise<AuditRecordPage> {
    return this.request('GET', '/v1/platform/audit/records', { query });
  }

  verifyChain(): Promise<{
    failures: ChainFailure[];
    records_checked: string;
    chains_checked: string;
    verdict: string;
    /** §42.3 — when the hashes were recomputed, not when the page was drawn. */
    verified_at?: string;
  }> {
    return this.request('GET', '/v1/platform/audit/verify');
  }

  /** An identity merge. A second member of platform staff approves it. */
  consolidateIdentities(body: {
    surviving_identity_id: string;
    superseded_identity_id: string;
    evidence: string;
    governing_deals: Record<string, string>;
  }): Promise<{
    identity_consolidation_id?: string;
    approval_request_id?: string;
    status?: string;
    note?: string;
    operators?: { operator_id: string; action: string }[];
  }> {
    return this.request('POST', '/v1/platform/identity-consolidations', { body });
  }

  identityConsolidations(status: 'PENDING' | 'APPROVED' | 'REJECTED'): Promise<{
    items: {
      id: string;
      operator_name: string | null;
      status: string;
      requested_by: string;
      requested_by_email: string | null;
      expired: boolean;
    }[];
  }> {
    return this.request('GET', '/v1/platform/identity-consolidations', { query: { status } });
  }

  approveIdentityConsolidation(id: string, reason: string): Promise<{ status: string }> {
    return this.request('POST', `/v1/platform/identity-consolidations/${id}/approve`, { body: { reason } });
  }

  rejectIdentityConsolidation(id: string, reason: string): Promise<{ status: string }> {
    return this.request('POST', `/v1/platform/identity-consolidations/${id}/reject`, { body: { reason } });
  }

  /**
   * §19.6 — a consumer that has stopped produces no error at all. Each cursor
   * also counts the events it has reached and those still ahead of it — for
   * one brand when `brandId` is given, for every brand otherwise.
   */
  consumers(query: { brandId?: string } = {}): Promise<ConsumersData> {
    return query.brandId
      ? this.request('GET', '/v1/platform/consumers', { query: { brand_id: query.brandId } })
      : this.request('GET', '/v1/platform/consumers');
  }

  /** Every operator profile one person holds, and the brands under each — for Suspend. */
  identityProfiles(identityId: string): Promise<{
    identity_id: string;
    display_name: string;
    identity_status: string;
    profiles: IdentityProfile[];
  }> {
    return this.request('GET', `/v1/platform/identities/${identityId}/profiles`);
  }

  /** Platform usage for one operator — staff pass operator_id. */
  platformUsage(operatorId: string): Promise<{
    items: { metric: string; period_start: string; units: string; overage_units: string }[];
    live: { brands: number; as_of: string };
    note: string;
  }> {
    return this.request('GET', '/v1/admin/platform/usage', { query: { operator_id: operatorId } });
  }

  platformInvoices(operatorId: string): Promise<{
    items: {
      id: string; number: string; period_start: string; period_end: string;
      net_minor: string; tax_minor: string; gross_minor: string; currency: string; state: string;
    }[];
    note: string;
  }> {
    return this.request('GET', '/v1/admin/platform/invoices', { query: { operator_id: operatorId } });
  }

  markPlatformInvoicePaid(
    operatorId: string,
    invoiceId: string,
    body: { reference: string; paid_on: string },
  ): Promise<{ mark_id: string }> {
    return this.request('POST', `/v1/admin/platform/invoices/${invoiceId}/settlement-marks`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /** §47.2 — charge terms and volume tiers for an operator. */
  chargeTerms(operatorId: string): Promise<{
    items: { id: string; model: string; flat_minor: string; rate_bps: number; currency: string }[];
    tiers: { charge_term_id: string; from_units: string; to_units: string | null; rate_bps: number | null; flat_minor: string | null }[];
    note: string;
  }> {
    return this.request('GET', '/v1/admin/platform/charge-terms', { query: { operator_id: operatorId } });
  }

  putChargeTier(
    operatorId: string,
    termId: string,
    body: { from_units: string; to_units?: string | null; rate_bps?: number; flat_minor?: string },
  ): Promise<Record<string, unknown>> {
    return this.request('PUT', `/v1/admin/platform/charge-terms/${termId}/tiers`, {
      query: { operator_id: operatorId },
      body,
    });
  }

  /** Item 6 (third batch) — a band is keyed by its own `from_units`. */
  deleteChargeTier(
    operatorId: string,
    termId: string,
    fromUnits: string,
  ): Promise<{ charge_term_id: string; from_units: string; deleted: boolean }> {
    return this.request('DELETE', `/v1/admin/platform/charge-terms/${termId}/tiers/${fromUnits}`, {
      query: { operator_id: operatorId },
    });
  }

  /**
   * Item 7 — products, jurisdictions, currencies, devices and OS, all
   * managed the same way: list (including inactive, for this screen),
   * create, edit, activate/deactivate, delete. `basePath` is one of
   * REF_DATA_TYPES below.
   */
  refDataList(basePath: string): Promise<{ items: Record<string, unknown>[] }> {
    return this.request('GET', basePath, { query: { include_inactive: 'true' } });
  }

  /**
   * Active currencies only — the dropdown `AddOperator` offers, not the
   * management screen.
   *
   * §1.3d — `minor_unit_scale` rides along and is read at boot as well as by
   * that dropdown: it is what tells `formatMoney` a JPY figure has no minor
   * unit. This console is where the column is edited, which is why it is also
   * the console where a wrong scale was hardest to see.
   */
  currencies(): Promise<{ items: { code: string; name: string; minor_unit_scale: number }[] }> {
    return this.request('GET', '/v1/platform/ref/currencies');
  }

  refDataCreate(basePath: string, body: Record<string, unknown>): Promise<{ code: string }> {
    return this.request('POST', basePath, { body });
  }

  refDataUpdate(basePath: string, code: string, body: Record<string, unknown>): Promise<{ code: string }> {
    return this.request('PUT', `${basePath}/${code}`, { body });
  }

  refDataSetActive(basePath: string, code: string, active: boolean): Promise<{ code: string; active: boolean }> {
    return this.request('PUT', `${basePath}/${code}/active`, { body: { active } });
  }

  refDataDelete(basePath: string, code: string): Promise<{ deleted: boolean; code: string }> {
    return this.request('DELETE', `${basePath}/${code}`);
  }

  /**
   * All of the file or none of it. On a rejection the failures come back in
   * `details.failures`, one per bad line, so the screen can show them all at
   * once rather than the first.
   */
  refDataImport(
    basePath: string,
    csv: string,
  ): Promise<{ added: number; updated: number; rows: number; message: string }> {
    return this.request('POST', `${basePath}/import`, { body: { csv } });
  }

  /** Item 8 — the flag catalogue, each with its global default and per-operator overrides. */
  featureFlags(): Promise<{
    items: {
      code: string;
      label: string;
      description: string;
      /** Which screen the flag governs — the list is grouped by it. */
      surface: string;
      global_enabled: boolean;
      overrides: { operator_id: string; operator: string; enabled: boolean }[];
    }[];
  }> {
    return this.request('GET', '/v1/platform/feature-flags');
  }

  /** A plain global on/off — safe for ON→OFF (clears overrides server-side either way). */
  setFeatureFlagGlobal(code: string, enabled: boolean): Promise<{ code: string; global_enabled: boolean }> {
    return this.request('PUT', `/v1/platform/feature-flags/${code}`, { body: { enabled } });
  }

  /** OFF→ON with an explicit operator selection — everyone else gets an explicit override, not the new default. */
  setFeatureFlagGlobalWithSelection(
    code: string,
    enabledOperatorIds: readonly string[],
  ): Promise<{ code: string; global_enabled: boolean; enabled_for: string[] }> {
    return this.request('PUT', `/v1/platform/feature-flags/${code}`, {
      body: { enabled: true, enabled_for: enabledOperatorIds },
    });
  }

  setFeatureFlagOperator(
    code: string,
    operatorId: string,
    enabled: boolean,
  ): Promise<{ code: string; operator_id: string; enabled: boolean }> {
    return this.request('PUT', `/v1/platform/feature-flags/${code}/operators/${operatorId}`, {
      body: { enabled },
    });
  }

  clearFeatureFlagOperator(code: string, operatorId: string): Promise<{ cleared: boolean }> {
    return this.request('DELETE', `/v1/platform/feature-flags/${code}/operators/${operatorId}`);
  }
}
